import { Link, useParams } from "react-router"
import { ArrowLeft, Download, RotateCcw } from "lucide-react"
import { useState } from "react"

import { useCreosUniqueBatch, useCreosUniqueJob, useRestartCreosUniqueJob } from "@/api/hooks/creos"
import { Button } from "@/components/ui/button"
import { usePageTitle } from "@/layouts/pageChrome"
import { triggerAuthenticatedDownload } from "@/lib/download"
import { cn } from "@/lib/utils"

function statusBadgeClass(status: string): string {
  switch (status) {
    case "completed":
      return "bg-emerald-500/20 text-emerald-300"
    case "failed":
      return "bg-red-500/20 text-red-300"
    case "processing":
      return "bg-amber-500/20 text-amber-300"
    case "pending":
      return "bg-sky-500/20 text-sky-300"
    default:
      return "bg-white/10 text-[#aaa]"
  }
}

export default function CreosUniquifyDetailPage() {
  const params = useParams()
  const id = (params.id || "").trim()
  usePageTitle("", id ? `CreOS uniquify ${id}` : "CreOS")
  const [restartError, setRestartError] = useState<string | null>(null)
  const restart = useRestartCreosUniqueJob()

  const { data: job, isLoading, error } = useCreosUniqueJob(id, {
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === "pending" || status === "processing" ? 5000 : false
    },
  })

  const batchId = job?.batch_id || ""
  const { data: batch } = useCreosUniqueBatch(batchId, {
    enabled: Boolean(batchId),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === "pending" ||
        status === "processing" ||
        status === "partial"
        ? 5000
        : false
    },
  })

  const batchZipReady =
    Boolean(batchId) &&
    (batch?.status === "completed" || batch?.status === "partial") &&
    Boolean(batch?.download_url) &&
    (batch?.completed_count ?? 0) > 0
  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          asChild
          variant="outline"
          className="border-[#555] bg-transparent text-white hover:bg-[#333]"
        >
          <Link to="/creos/uniquify">
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
            Back
          </Link>
        </Button>
        {job ? (
          <span
            className={cn(
              "inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
              statusBadgeClass(job.status),
            )}
          >
            {job.status}
          </span>
        ) : null}
        {job?.status === "completed" && job.download_url ? (
          <Button
            type="button"
            variant="outline"
            className="ml-auto border-[#555] bg-transparent text-white hover:bg-[#333]"
            onClick={() => {
              const name =
                job.media_kind === "carousel" || job.file_count > 1
                  ? `creos-unique-${job.public_id}.zip`
                  : job.s3_urls?.[0]?.filename || `creos-unique-${job.public_id}`
              triggerAuthenticatedDownload(job.download_url!, name)
            }}
          >
            <Download className="mr-2 h-4 w-4" aria-hidden />
            {job.media_kind === "carousel" || job.file_count > 1
              ? "Download ZIP"
              : "Download"}
          </Button>
        ) : null}
        {job?.batch_id ? (
          <Button
            type="button"
            variant="outline"
            aria-disabled={!batchZipReady}
            title={batchZipReady ? undefined : "Wait until finished"}
            className={cn(
              "border-[#555] bg-transparent text-white hover:bg-[#333]",
              !(job.status === "completed" && job.download_url) && "ml-auto",
              !batchZipReady &&
                "cursor-not-allowed opacity-40 hover:bg-transparent",
            )}
            onClick={() => {
              if (!batchZipReady || !batch?.download_url) return
              const short = job.batch_id!.replace(/-/g, "").slice(0, 8)
              triggerAuthenticatedDownload(
                batch.download_url,
                `creos-batch-${short}.zip`,
              )
            }}
          >
            <Download className="mr-2 h-4 w-4" aria-hidden />
            Batch ZIP
          </Button>
        ) : null}
        {job?.batch_id ? (
          <Button
            asChild
            variant="outline"
            className="border-[#555] bg-transparent text-white hover:bg-[#333]"
          >
            <Link to={`/creos/uniquify/batch/${job.batch_id}`}>Batch</Link>
          </Button>
        ) : null}
        {job?.status === "failed" ? (
          <Button
            type="button"
            variant="outline"
            disabled={restart.isPending}
            className="ml-auto border-[#555] bg-transparent text-white hover:bg-[#333]"
            onClick={() => {
              setRestartError(null)
              restart.mutate(job.public_id, {
                onError: (err) => {
                  setRestartError(err instanceof Error ? err.message : "Failed to restart")
                },
              })
            }}
          >
            <RotateCcw className="mr-2 h-4 w-4" aria-hidden />
            {restart.isPending ? "Restarting…" : "Restart"}
          </Button>
        ) : null}
      </div>

      {restartError ? (
        <p className="text-sm text-red-300">{restartError}</p>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-[#aaa]">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-300">{error.message}</p>
      ) : !job ? (
        <p className="text-sm text-[#aaa]">Job not found</p>
      ) : (
        <>
          <div className="rounded-xl border border-[#4a4a4a] bg-[#2d2d2d] p-6">
            <h1 className="font-mono text-xl font-semibold text-white">
              {job.public_id}
            </h1>
            <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-[#888]">Type</dt>
                <dd className="capitalize text-white">{job.media_kind}</dd>
              </div>
              <div>
                <dt className="text-[#888]">Files</dt>
                <dd className="text-white">{job.file_count}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-[#888]">Source</dt>
                <dd className="mt-1 text-[#ddd]">
                  {job.source_filenames.join(", ") || "—"}
                </dd>
              </div>
              {job.error_message ? (
                <div className="sm:col-span-2">
                  <dt className="text-[#888]">Error</dt>
                  <dd className="mt-1 text-red-300">{job.error_message}</dd>
                </div>
              ) : null}
            </dl>
          </div>

          {job.status === "pending" || job.status === "processing" ? (
            <p className="text-sm text-[#aaa]">
              {job.media_kind === "video"
                ? "Re-encoding video… this can take 1–4 minutes. This page refreshes automatically."
                : "Processing… this page refreshes automatically."}
            </p>
          ) : null}

          {job.status === "completed" && job.s3_urls?.length ? (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white">Result</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {job.s3_urls.map((item, idx) => {
                  const isVideo = item.content_type.startsWith("video/")
                  return (
                    <div
                      key={item.key || idx}
                      className="overflow-hidden rounded-xl border border-[#4a4a4a] bg-[#2d2d2d]"
                    >
                      {isVideo ? (
                        <video
                          src={item.url}
                          controls
                          playsInline
                          preload="metadata"
                          className="aspect-[9/16] w-full bg-black object-contain"
                        />
                      ) : (
                        <a href={item.url} target="_blank" rel="noreferrer">
                          <img
                            src={item.url}
                            alt={item.filename}
                            className="w-full bg-[#1a1a1a] object-contain"
                          />
                        </a>
                      )}
                      <div className="flex items-center justify-between gap-2 p-3">
                        <span className="truncate text-sm text-[#ccc]">
                          {item.filename}
                        </span>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 rounded-md border border-[#555] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#333]"
                          onClick={() => {
                            triggerAuthenticatedDownload(
                              item.download_url,
                              item.filename,
                            )
                          }}
                        >
                          <Download className="h-3.5 w-3.5" aria-hidden />
                          Download
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
